import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { API } from "@/lib/api";

const segments = {
  corporate: {
    label: "For Corporates",
    desc: "Term loans, working capital, NCDs, ECBs & invoice discounting",
    types: [
      "Term Loan",
      "Working Capital Demand Loan",
      "Non-Convertible Debenture",
      "External Commercial Borrowing",
      "Purchase Invoice Discounting",
      "Sales Invoice Discounting",
    ],
  },
  retail: {
    label: "For Individuals & SMEs",
    desc: "Home, business, LAP & gold loans",
    types: ["Home Loan", "Business Loan", "Loan Against Property", "Gold Loan"],
  },
};

const initial = { name: "", email: "", phone: "", loan_type: "", amount: "", city: "", message: "" };

export const EnquirySection = () => {
  const [form, setForm] = useState(initial);
  const [consent, setConsent] = useState(false);
  const [submittedRef, setSubmittedRef] = useState(null);
  const [segment, setSegment] = useState("corporate");
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaHost = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    const render = () => {
      if (!captchaHost.current || !window.grecaptcha?.render || widgetId.current !== null) return;
      widgetId.current = window.grecaptcha.render(captchaHost.current, {
        sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        theme: "dark",
        callback: (t) => setCaptchaToken(t),
        "expired-callback": () => setCaptchaToken(""),
        "error-callback": () => setCaptchaToken(""),
      });
    };
    if (window.grecaptcha?.render) {
      render();
      return;
    }
    if (!document.querySelector("script[data-recaptcha]")) {
      const s = document.createElement("script");
      s.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      s.async = true;
      s.defer = true;
      s.dataset.recaptcha = "1";
      document.head.appendChild(s);
    }
    const interval = setInterval(() => {
      if (window.grecaptcha?.render) {
        clearInterval(interval);
        render();
      }
    }, 200);
    return () => clearInterval(interval);
  }, []);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => {
    const v = e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const normalizedPhone = () => {
    let d = form.phone.replace(/\D/g, "");
    if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
    if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
    return d;
  };

  const validate = () => {
    const errs = {};
    const name = form.name.trim();
    if (!/^[A-Za-z][A-Za-z\s.&'-]{1,99}$/.test(name)) {
      errs.name = "Enter a valid name — letters, spaces and . & ' - only";
    } else if (segment === "corporate" && !/\b(pvt\.?\s*ltd|private\s+limited|ltd\.?|limited|llp|opc)\b/i.test(name)) {
      errs.name = "Enter the registered company name, e.g. Sharma Industries Pvt Ltd";
    }
    if (!/^[6-9]\d{9}$/.test(normalizedPhone())) errs.phone = "Enter a valid 10-digit Indian mobile number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) errs.email = "Enter a valid email address";
    if (!/^[A-Za-z][A-Za-z\s.'-]{1,49}$/.test(form.city.trim())) errs.city = "Enter a valid city name";
    const amt = parseFloat(form.amount);
    if (!amt || amt < 10000 || amt > 10000000000) errs.amount = "Enter an amount between ₹10,000 and ₹1,000 crore";
    if (!form.loan_type) errs.loan_type = "Please select a loan type";
    return errs;
  };

  const FieldError = ({ id }) =>
    errors[id] ? (
      <p data-testid={`error-${id}`} className="text-xs text-red-400 mt-1.5">{errors[id]}</p>
    ) : null;

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please fix the highlighted fields");
      return;
    }
    if (!consent) {
      toast.error("Please agree to the Privacy Policy to submit");
      return;
    }
    if (!captchaToken) {
      toast.error("Please complete the CAPTCHA");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/enquiries`, {
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        phone: `+91${normalizedPhone()}`,
        amount: parseFloat(form.amount),
        consent,
        segment,
        captcha_token: captchaToken,
      });
      setSubmittedRef(res.data.id);
      toast.success("Enquiry received. We'll reach out within 1–2 business days.");
      setForm(initial);
      setErrors({});
      setConsent(false);
      setCaptchaToken("");
      if (widgetId.current !== null) window.grecaptcha?.reset(widgetId.current);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
            ? detail[0].msg.replace(/^Value error,\s*/i, "")
            : "Could not submit. Please try again.";
      toast.error(msg);
      setCaptchaToken("");
      if (widgetId.current !== null) window.grecaptcha?.reset(widgetId.current);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquire" data-testid="enquiry-section" className="py-14 lg:py-20 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <p className="overline-label mb-4">Start the conversation</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            Tell us what you're building.
          </h2>
          <p className="mt-6 text-[#A0AAB2] text-base md:text-lg leading-relaxed">
            Share a few details and we'll respond within 1–2 business days with an honest read
            on eligibility, expected pricing and the right lenders to approach.
          </p>
          <div className="mt-12 space-y-6 text-sm">
            <div className="flex items-center gap-4 text-[#A0AAB2]">
              <Phone size={18} className="text-gold" />
              <a data-testid="contact-phone" href="tel:+918796512777" className="hover:text-gold transition-colors">+91 87965 12777</a>
              <a
                data-testid="contact-whatsapp"
                href="https://wa.me/918796512777"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat on WhatsApp"
                className="text-[#25D366] hover:scale-110 transition-transform"
              >
                <WhatsAppIcon size={20} />
              </a>
            </div>
            <a data-testid="contact-email" href="mailto:info@globusfinancialservices-in.com" className="flex items-center gap-4 text-[#A0AAB2] hover:text-gold transition-colors">
              <Mail size={18} className="text-gold" /> info@globusfinancialservices-in.com
            </a>
            <p data-testid="contact-address" className="flex items-center gap-4 text-[#A0AAB2]">
              <MapPin size={18} className="text-gold shrink-0 mt-1" />
              <span>HC-3, 3rd Floor, Durga Apartment<br />761 Shalimar Garden Ext-1, Sahibabad<br />Ghaziabad, Uttar Pradesh-201005</span>
            </p>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={submit}
          noValidate
          data-testid="enquiry-form"
          className="lg:col-span-7 bg-slate2 border border-white/10 p-8 lg:p-10 space-y-6"
        >
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">Step 1 — Who is this for?</p>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="segment-selector">
              {Object.entries(segments).map(([key, s]) => (
                <button
                  type="button"
                  key={key}
                  data-testid={`segment-${key}`}
                  onClick={() => {
                    setSegment(key);
                    setForm((f) => ({ ...f, loan_type: "" }));
                  }}
                  className={`text-left border rounded-sm p-4 transition-colors ${
                    segment === key ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/25"
                  }`}
                >
                  <p className={`text-sm font-semibold ${segment === key ? "text-gold" : "text-foreground"}`}>{s.label}</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{s.desc}</p>
                </button>
              ))}
            </div>
          </div>
          {submittedRef && (
            <div data-testid="enquiry-reference" className="bg-gold/10 border border-gold/40 rounded-sm p-5 text-sm">
              <p className="text-gold font-semibold mb-1">Enquiry submitted successfully</p>
              <p className="text-[#A0AAB2] leading-relaxed">
                Your reference ID is{" "}
                <span data-testid="enquiry-reference-id" className="text-foreground font-mono font-semibold tracking-wider">
                  {submittedRef.slice(0, 8).toUpperCase()}
                </span>
                . Save it to track your application status anytime.
              </p>
              <Link data-testid="enquiry-track-link" to="/track" className="inline-block mt-2 text-gold hover:underline text-xs tracking-wide">
                Track your application →
              </Link>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="eq-name" className="text-sm text-[#A0AAB2] mb-2 block">
                {segment === "corporate" ? "Company name" : "Full name"}
              </Label>
              <Input data-testid="enquiry-name-input" id="eq-name" required value={form.name} onChange={set("name")} placeholder={segment === "corporate" ? "Sharma Industries Pvt Ltd" : "Aarav Sharma"} className={`bg-slate3 rounded-sm border ${errors.name ? "border-red-400" : "border-white/10"}`} />
              <FieldError id="name" />
            </div>
            <div>
              <Label htmlFor="eq-phone" className="text-sm text-[#A0AAB2] mb-2 block">Mobile number</Label>
              <Input data-testid="enquiry-phone-input" id="eq-phone" required value={form.phone} onChange={set("phone")} placeholder="98XXX XXXXX" className={`bg-slate3 rounded-sm border ${errors.phone ? "border-red-400" : "border-white/10"}`} />
              <FieldError id="phone" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="eq-email" className="text-sm text-[#A0AAB2] mb-2 block">Email</Label>
              <Input data-testid="enquiry-email-input" id="eq-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className={`bg-slate3 rounded-sm border ${errors.email ? "border-red-400" : "border-white/10"}`} />
              <FieldError id="email" />
            </div>
            <div>
              <Label htmlFor="eq-city" className="text-sm text-[#A0AAB2] mb-2 block">City</Label>
              <Input data-testid="enquiry-city-input" id="eq-city" required value={form.city} onChange={set("city")} placeholder="Mumbai" className={`bg-slate3 rounded-sm border ${errors.city ? "border-red-400" : "border-white/10"}`} />
              <FieldError id="city" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="eq-type" className="text-sm text-[#A0AAB2] mb-2 block">Loan type</Label>
              <Select value={form.loan_type} onValueChange={set("loan_type")}>
                <SelectTrigger data-testid="enquiry-loan-type-select" id="eq-type" className="bg-slate3 border-white/10 rounded-sm">
                  <SelectValue placeholder="Select a loan type" />
                </SelectTrigger>
                <SelectContent className="bg-slate2 border-white/10">
                  {segments[segment].types.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`enquiry-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError id="loan_type" />
            </div>
            <div>
              <Label htmlFor="eq-amount" className="text-sm text-[#A0AAB2] mb-2 block">Amount sought (₹)</Label>
              <Input data-testid="enquiry-amount-input" id="eq-amount" type="number" min="1" required value={form.amount} onChange={set("amount")} placeholder="5000000" className={`bg-slate3 rounded-sm border ${errors.amount ? "border-red-400" : "border-white/10"}`} />
              <FieldError id="amount" />
            </div>
          </div>
          <div>
            <Label htmlFor="eq-message" className="text-sm text-[#A0AAB2] mb-2 block">Anything we should know? (optional)</Label>
            <Textarea data-testid="enquiry-message-input" id="eq-message" rows={3} maxLength={50} value={form.message} onChange={set("message")} placeholder="Purpose and timeline, briefly" className="bg-slate3 border-white/10 rounded-sm" />
            <p data-testid="enquiry-message-counter" className="text-[11px] text-muted-foreground mt-1.5 text-right">{form.message.length}/50 characters</p>
          </div>
          <div ref={captchaHost} data-testid="enquiry-captcha" className="recaptcha-dark" aria-label="reCAPTCHA" />
          <div className="flex items-start gap-3">
            <Checkbox
              data-testid="enquiry-consent-checkbox"
              id="eq-consent"
              checked={consent}
              onCheckedChange={(v) => setConsent(!!v)}
              className="mt-0.5 border-white/20 data-[state=checked]:bg-gold data-[state=checked]:text-night data-[state=checked]:border-gold"
            />
            <Label htmlFor="eq-consent" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
              I agree to the <Link data-testid="enquiry-privacy-link" to="/privacy-policy" className="text-gold hover:underline">Privacy Policy</Link> and consent to Globus Financial Services contacting me about my enquiry.
            </Label>
          </div>
          <button
            data-testid="enquiry-submit-button"
            type="submit"
            disabled={loading}
            className="bg-gold text-night hover:bg-goldhover transition-colors rounded-sm px-8 py-3.5 font-semibold flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />}
            {loading ? "Submitting…" : "Submit enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
